/**
 * @fileoverview Form step component for interactive team chat flow.
 * Wraps the reusable FormRenderer in the chat context with team branding.
 */

import { useRef } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Loader2 } from "lucide-react";
import { FormRenderer } from "@/components/agents/interfaces/form-renderer";
import type { FormSchema } from "@/types/agent-ui-types";

interface InteractiveFormStepProps {
  /** Team identifier */
  teamId: string;
  /** The form schema to render */
  formSchema: FormSchema;
  /** Display name of the form */
  formName: string;
  /** Called when user submits the form with validated values */
  onSubmit: (values: Record<string, unknown>) => void;
  /** Whether the form is currently submitting (chat stream starting) */
  isSubmitting: boolean;
}

/**
 * Renders the team's default intake form before chat begins.
 * Wraps the generic FormRenderer in a centered card layout with a submit button.
 */
export function InteractiveFormStep({
  formSchema,
  formName,
  onSubmit,
  isSubmitting,
}: InteractiveFormStepProps) {
  const formRef = useRef<HTMLFormElement>(null);

  const submitLabel = formSchema.submitButton?.label ?? "Submit";
  const loadingLabel = formSchema.submitButton?.loadingLabel ?? "Submitting...";

  return (
    <div className="flex size-full flex-col items-center justify-center bg-background p-4 sm:p-8">
      <Card className="mx-auto flex w-full max-w-xl flex-col max-h-[calc(100svh-4rem)]">
        <CardHeader className="shrink-0 border-b">
          <CardTitle className="text-lg">{formName}</CardTitle>
          <CardDescription>
            Fill out this form to get started. Your responses will help us provide personalized guidance.
          </CardDescription>
        </CardHeader>

        <CardContent className="flex-1 overflow-y-auto py-6">
          <FormRenderer
            ref={formRef}
            schema={formSchema}
            onSubmit={onSubmit}
            isSubmitting={isSubmitting}
          />
        </CardContent>

        <div className="shrink-0 border-t px-6 py-4">
          <Button
            type="button"
            className="w-full"
            disabled={isSubmitting}
            onClick={() => formRef.current?.requestSubmit()}
          >
            {isSubmitting ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                {loadingLabel}
              </>
            ) : (
              submitLabel
            )}
          </Button>
        </div>
      </Card>
    </div>
  );
}
